/**
 * iter v28 r301 — Broadcast WhatsApp Assistito.
 *
 * Modale per inviare UN messaggio (testo libero) a un gruppo di contatti,
 * con personalizzazione AI per ciascun destinatario (nome, città, eventuale
 * immobile allegato). NON invia da server: apre WhatsApp in sequenza,
 * uno per uno. L'agente resta in controllo.
 *
 * Differenza vs BulkGreetingsModal: questo è per comunicazioni quotidiane
 * (nuovo annuncio, Open House, novità studio), non per festività.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   prefillPropertyId?: string  // se aperto da una property, pre-seleziona
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { WhatsappLogo, Spinner, X, ArrowRight, Check, Megaphone, House } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const AUDIENCE_OPTIONS = [
  { v: "all", label: "Tutti", hint: "Tutti i contatti con telefono" },
  { v: "caldo", label: "Solo caldi", hint: "Clienti pronti / interessati" },
  { v: "tiepido", label: "Solo tiepidi", hint: "Clienti che si stanno guardando intorno" },
  { v: "compatibili", label: "Compatibili", hint: "Solo chi cerca un immobile come quello allegato" },
];

export default function BroadcastModal({ open, onClose, prefillPropertyId }) {
  const [phase, setPhase] = useState("compose"); // compose | loading | sending
  const [text, setText] = useState("");
  const [audience, setAudience] = useState("all");
  const [propertyId, setPropertyId] = useState(prefillPropertyId || "");
  const [properties, setProperties] = useState([]);
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [sentIds, setSentIds] = useState({});
  const [contactCount, setContactCount] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPhase("compose");
    setText("");
    setAudience("all");
    setPropertyId(prefillPropertyId || "");
    setItems([]);
    setActiveIdx(0);
    setSentIds({});

    (async () => {
      try {
        const [ctR, prR] = await Promise.all([
          api.get("/contacts"),
          api.get("/properties"),
        ]);
        const cs = ctR.data?.items || ctR.data || [];
        setContactCount(cs.filter((c) => c.phone).length);
        const ps = prR.data?.items || prR.data || [];
        setProperties(ps.filter((p) => !p.is_deleted));
      } catch {
        // silent
      }
    })();
  }, [open, prefillPropertyId]);

  if (!open) return null;

  const submit = async () => {
    if (text.trim().length < 8) {
      toast.error("Il messaggio deve essere più descrittivo (min 8 caratteri).");
      return;
    }
    if (audience === "compatibili" && !propertyId) {
      toast.error("Per 'Compatibili' devi allegare un immobile.");
      return;
    }
    setPhase("loading");
    try {
      const { data } = await api.post("/contacts/bulk-greetings", {
        occasion: "broadcast",
        custom_text: text.trim(),
        audience,
        property_id: propertyId || null,
      });
      const its = data?.items || [];
      if (its.length === 0) {
        toast.warning("Nessun contatto raggiungibile con questo filtro.");
        setPhase("compose");
        return;
      }
      setItems(its);
      setActiveIdx(0);
      setPhase("sending");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generazione fallita";
      toast.error(typeof msg === "string" ? msg : "Generazione fallita");
      setPhase("compose");
    }
  };

  const markSentAndNext = () => {
    const cur = items[activeIdx];
    if (!cur) return;
    setSentIds((m) => ({ ...m, [cur.contact_id]: true }));
    if (activeIdx + 1 < items.length) {
      setActiveIdx((i) => i + 1);
    } else {
      toast.success(`Inviati ${items.length} messaggi a tutti i contatti`);
      onClose?.();
    }
  };

  const openWhatsApp = (it) => {
    if (it.wa_url) window.open(it.wa_url, "_blank");
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="broadcast-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b border-[#EAE7DF] flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#7C8B6E]/15 text-[#5A6A4D] flex items-center justify-center">
              <Megaphone size={16} weight="bold" />
            </div>
            <div>
              <h3 className="font-semibold text-[15px] leading-tight" data-testid="broadcast-title">Broadcast WhatsApp</h3>
              <p className="text-[11.5px] text-[#6B6A65] leading-tight">
                Stesso messaggio, personalizzato per ognuno · uno per uno
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F4F1E8]" data-testid="broadcast-close">
            <X size={16} />
          </button>
        </div>

        {/* Phase: compose */}
        {phase === "compose" && (
          <div className="px-5 py-4 space-y-4 overflow-y-auto" data-testid="broadcast-compose-phase">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-2">
                A chi
              </label>
              <div className="grid grid-cols-2 gap-2">
                {AUDIENCE_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => setAudience(o.v)}
                    className={`text-left px-3 py-2 rounded-md border transition-all ${
                      audience === o.v
                        ? "border-[#7C8B6E] bg-[#F1F4ED]"
                        : "border-[#EAE7DF] bg-white hover:border-[#7C8B6E]/40"
                    }`}
                    data-testid={`broadcast-audience-${o.v}`}
                    aria-pressed={audience === o.v}
                  >
                    <div className="text-[13px] font-semibold text-[#1C1C1A]">{o.label}</div>
                    <div className="text-[11px] text-[#6B6A65] mt-0.5">{o.hint}</div>
                  </button>
                ))}
              </div>
              {contactCount !== null && (
                <p className="text-[11.5px] text-[#6B6A65] mt-2 italic" data-testid="broadcast-contact-count">
                  Hai {contactCount} {contactCount === 1 ? "contatto" : "contatti"} raggiungibili in rubrica.
                </p>
              )}
            </div>

            {/* Property attach */}
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">
                Allega immobile <span className="normal-case tracking-normal text-[#9A968B]/70 font-normal">(facoltativo)</span>
              </label>
              <div className="relative">
                <House size={14} className="absolute top-3 left-2.5 text-[#9A968B]" />
                <select
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  className="w-full pl-8 pr-3 py-2.5 bg-[#FAFAF7] border border-[#E8E5DE] rounded-md text-[13px] focus:outline-none focus:border-[#7C8B6E] appearance-none"
                  data-testid="broadcast-property-select"
                >
                  <option value="">Nessun immobile allegato</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title || p.address || p.id} {p.price ? `· € ${p.price.toLocaleString("it-IT")}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-[#9A968B] mt-1">
                Se allegato, Pronto aggiunge in coda al messaggio il link alla vetrina personale.
              </p>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">
                Il tuo messaggio
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={"Esempio: Sono lieto di presentarvi un nuovo trilocale ristrutturato in zona Brera. 280.000 euro. Disponibile per visite questo weekend."}
                rows={4}
                className="w-full px-3 py-2.5 bg-[#FAFAF7] border border-[#E8E5DE] rounded-md text-[13px] focus:outline-none focus:border-[#7C8B6E] resize-none"
                data-testid="broadcast-text-input"
                maxLength={500}
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-[11px] text-[#9A968B]">
                  Pronto lo riscrive in tono formale (Lei) e cambia nome/firma per ogni contatto.
                </p>
                <span className="text-[10.5px] text-[#9A968B]">{text.length}/500</span>
              </div>
            </div>

            <button
              onClick={submit}
              disabled={text.trim().length < 8}
              className="w-full bg-[#1C1C1A] hover:bg-[#3F3D38] disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium py-3 rounded-md flex items-center justify-center gap-2 transition-colors"
              data-testid="broadcast-generate-btn"
            >
              <Megaphone size={15} weight="bold" />
              Prepara i messaggi
            </button>
          </div>
        )}

        {/* Phase: loading */}
        {phase === "loading" && (
          <div className="px-5 py-12 flex flex-col items-center gap-3" data-testid="broadcast-loading-phase">
            <Spinner size={28} className="animate-spin text-[#7C8B6E]" />
            <p className="text-[13px] text-[#6B6A65]">Pronto sta scrivendo un messaggio personalizzato per ogni contatto…</p>
            <p className="text-[11px] text-[#9A968B] italic">Può richiedere fino a 30 secondi.</p>
          </div>
        )}

        {/* Phase: sending */}
        {phase === "sending" && items[activeIdx] && (
          <div className="flex-1 overflow-y-auto" data-testid="broadcast-sending-phase">
            <div className="px-5 py-3 bg-[#F1F4ED] border-b border-[#7C8B6E]/20 flex items-center justify-between">
              <div className="text-[12px] text-[#5A6A4D] font-medium">
                {activeIdx + 1} di {items.length}
              </div>
              <div className="text-[11px] text-[#5A6A4D]">
                Inviati: {Object.keys(sentIds).length} / {items.length}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold mb-1">A</div>
              <div className="text-[15px] font-semibold text-[#1C1C1A]" data-testid={`broadcast-recipient-${activeIdx}`}>
                {items[activeIdx].name || "Senza nome"}
              </div>
              <div className="text-[12.5px] text-[#6B6A65] mb-3">{items[activeIdx].phone}</div>
              <div className="bg-[#FAFAF7] border border-[#E8E5DE] rounded-md p-3 text-[13px] text-[#1C1C1A] whitespace-pre-line leading-relaxed" data-testid={`broadcast-message-preview-${activeIdx}`}>
                {items[activeIdx].message}
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => openWhatsApp(items[activeIdx])}
                  className="flex-1 bg-[#25D366] hover:bg-[#1FA854] text-white font-medium py-2.5 rounded-md flex items-center justify-center gap-2"
                  data-testid="broadcast-open-wa-btn"
                >
                  <WhatsappLogo size={15} weight="fill" />
                  Apri WhatsApp
                </button>
                <button
                  onClick={markSentAndNext}
                  className="px-3 py-2.5 border border-[#E8E5DE] hover:border-[#7C8B6E] rounded-md text-[#1C1C1A] font-medium flex items-center gap-1.5"
                  data-testid="broadcast-next-btn"
                >
                  {activeIdx + 1 === items.length ? <Check size={14} /> : <ArrowRight size={14} />}
                  {activeIdx + 1 === items.length ? "Fine" : "Inviato, avanti"}
                </button>
              </div>
              <p className="text-[11px] text-[#9A968B] italic mt-3 leading-snug">
                Apre WhatsApp con il messaggio pre-compilato. Tu controlli, ritocchi se vuoi, e premi invio nell'app.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
